/**
 * The sun's altitude above the horizon, in DEGREES, for the Auto theme mode.
 *
 * Same rule as the phone's Auto theme: dark once the sun is below 6° (civil-twilight-ish, when a
 * bright screen starts to glare), light above it. A low-precision solar position (good to a fraction
 * of a degree), which is plenty for choosing a colour scheme and saves a dependency.
 */

const RAD = Math.PI / 180

/** Altitude of the sun's centre above the horizon in degrees (negative below it). */
export function sunAltitudeDegrees(date: Date, latitude: number, longitude: number): number {
  // Days since J2000.0
  const d = date.getTime() / 86_400_000 - 10_957.5
  const g = (357.529 + 0.98560028 * d) * RAD // mean anomaly
  const q = 280.459 + 0.98564736 * d // mean longitude, degrees
  const L = (q + 1.915 * Math.sin(g) + 0.02 * Math.sin(2 * g)) * RAD // ecliptic longitude
  const e = (23.439 - 0.00000036 * d) * RAD // obliquity
  const ra = Math.atan2(Math.cos(e) * Math.sin(L), Math.cos(L))
  const dec = Math.asin(Math.sin(e) * Math.sin(L))
  // Greenwich mean sidereal time, degrees
  const gmst = 280.46061837 + 360.98564736629 * d
  const hourAngle = (gmst + longitude) * RAD - ra
  const lat = latitude * RAD
  const sinAlt = Math.sin(lat) * Math.sin(dec) + Math.cos(lat) * Math.cos(dec) * Math.cos(hourAngle)
  return Math.asin(Math.max(-1, Math.min(1, sinAlt))) / RAD
}

/** Below this the Auto theme goes dark. */
export const AUTO_DARK_BELOW_DEGREES = 6

export function autoThemeIsDark(date: Date, latitude: number, longitude: number): boolean {
  return sunAltitudeDegrees(date, latitude, longitude) < AUTO_DARK_BELOW_DEGREES
}
